// Core metrics API — everything is built on rpc_metric_rollup.
import { supabase } from "@/lib/supabase";
import { deltaPct } from "@/lib/format";
import type { RollupRow, ActivityFilter, RegionFilter, BrandFilter, ChannelFilter, CurrencyCode } from "@/lib/api/types";

export interface RollupQuery {
  from: string;          // yyyy-mm-dd inclusive
  to: string;            // yyyy-mm-dd inclusive
  activity: ActivityFilter;
  region: RegionFilter;
  brand: BrandFilter;
  channel: ChannelFilter;
  /** Currency of returned monetary values; local is only for region groups. */
  currency?: CurrencyCode | "local";
}

export type RollupGroup = "total" | "date" | "brand" | "platform" | "region" | "activity";

/** Derived KPIs, keyed by metric_catalog.key (plus `mer`). null = undefined ratio. */
export interface Kpis {
  spend: number;
  revenue: number;
  paid_revenue: number;
  orders: number;
  roas: number | null;
  mer: number | null;
  aov: number | null;
  conversion_rate: number | null;
  paid_engagement_rate: number | null;
  impressions: number;
  clicks: number;
  ctr: number | null;
  reached_accounts: number;
  organic_engagement_rate: number | null;
  follower_growth: number;
  followers_total: number;
  content_pieces: number;
  video_views: number;
  applications: number;
  cost_per_application: number | null;
  new_customers: number;
  new_customer_revenue: number;
  cpa_new_customer: number | null;
  /** Cost per ad-attributed order: paid spend ÷ paid orders (the platforms' "cost per purchase"). */
  cpa: number | null;
  spend_pct_of_new_revenue: number | null;
  branded_search: number;
  website_visitors: number;
  /** Revenue vs the previous equal-length window. Set by getTotals only. */
  net_revenue_growth: number | null;
}

const div = (a: number, b: number): number | null => (b > 0 ? a / b : null);

const EMPTY_ROW: RollupRow = {
  group_key: "total", group_label: "total", spend: 0, revenue: 0, paid_revenue: 0,
  paid_spend: 0, paid_orders: 0, paid_clicks: 0, paid_impressions: 0, paid_engagements: 0,
  orders: 0, clicks: 0, impressions: 0, engagements: 0, reach: 0,
  video_views: 0, conversions: 0, followers_delta: 0, followers_total: 0, content_pieces: 0, applications: 0,
  new_customers: 0, new_customer_revenue: 0, branded_search: 0, website_visitors: 0, n_rows: 0,
};

export function deriveKpis(r: RollupRow): Kpis {
  return {
    spend: r.spend,
    revenue: r.revenue,
    paid_revenue: r.paid_revenue,
    orders: r.orders,
    // A selection with no orders and no revenue at all (Atelier Luxus, Maison
    // Vervloet: no web shop) has no return to measure — a dash, not 0.00×.
    roas: r.revenue > 0 || r.orders > 0 ? div(r.paid_revenue, r.paid_spend) : null,
    mer: r.revenue > 0 || r.orders > 0 ? div(r.revenue, r.spend) : null,
    aov: div(r.revenue, r.orders),
    // Ratios are computed on paid traffic only — owned channels (Klaviyo)
    // carry orders/revenue with no ad clicks and would distort them.
    conversion_rate: div(r.paid_orders, r.paid_clicks),
    paid_engagement_rate: div(r.paid_engagements, r.paid_impressions),
    impressions: r.impressions,
    clicks: r.clicks,
    ctr: div(r.paid_clicks, r.paid_impressions),
    // Sums of DAILY reach: right for one day (the charts), never a period total —
    // a person reached on five days counts five times. Pages take period reach
    // and ER from useExactReach (each platform's own deduplicated figure).
    reached_accounts: r.reach,
    organic_engagement_rate: div(r.engagements, r.reach),
    follower_growth: r.followers_delta,
    followers_total: r.followers_total,
    content_pieces: r.content_pieces,
    video_views: r.video_views,
    applications: r.applications,
    cost_per_application: div(r.spend, r.applications),
    new_customers: r.new_customers,
    new_customer_revenue: r.new_customer_revenue,
    cpa_new_customer: div(r.spend, r.new_customers),
    cpa: div(r.paid_spend, r.paid_orders),
    spend_pct_of_new_revenue: div(r.spend, r.new_customer_revenue),
    branded_search: r.branded_search,
    website_visitors: r.website_visitors,
    net_revenue_growth: null, // period-over-period; filled by getTotals
  };
}

async function rollup(q: RollupQuery, group: RollupGroup): Promise<RollupRow[]> {
  const { data, error } = await supabase.rpc("rpc_metric_rollup_currency", {
    p_from: q.from,
    p_to: q.to,
    p_activity: q.activity,
    p_region: q.region,
    p_brand: q.brand === "all" ? null : q.brand,
    p_platform: q.channel === "all" ? null : q.channel,
    p_group: group,
    p_currency: q.currency ?? "EUR",
  });
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>) => Object.fromEntries(
    Object.entries(row).map(([k, v]) => [k, k === "group_key" || k === "group_label" ? v : Number(v ?? 0)]),
  )) as RollupRow[];
}

/** Window length in whole days (inclusive). */
function windowDays(q: RollupQuery): number {
  const ms = new Date(q.to).getTime() - new Date(q.from).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

/** Returns the immediately-preceding window of equal length. */
function previousWindow(q: RollupQuery): RollupQuery {
  const len = windowDays(q);
  const prevTo = new Date(new Date(q.from).getTime() - 86_400_000);
  const prevFrom = new Date(prevTo.getTime() - (len - 1) * 86_400_000);
  return { ...q, from: prevFrom.toISOString().slice(0, 10), to: prevTo.toISOString().slice(0, 10) };
}

export interface TotalsResult {
  raw: RollupRow;
  kpis: Kpis;
  /** Same KPIs for the previous equal-length window (for trend deltas). */
  prev: Kpis;
}

export async function getTotals(q: RollupQuery): Promise<TotalsResult> {
  const [cur, prev] = await Promise.all([rollup(q, "total"), rollup(previousWindow(q), "total")]);
  const curRow = cur[0] ?? EMPTY_ROW;
  const prevRow = prev[0] ?? EMPTY_ROW;
  const kpis = deriveKpis(curRow);
  // Net revenue growth = current revenue vs the previous equal-length window.
  kpis.net_revenue_growth = deltaPct(curRow.revenue, prevRow.revenue);
  return { raw: curRow, kpis, prev: deriveKpis(prevRow) };
}

export interface SeriesPoint extends RollupRow, Kpis {
  date: string;
}

/** Daily time series (chronological), each point carrying raw + derived values. */
export async function getDailySeries(q: RollupQuery): Promise<SeriesPoint[]> {
  const rows = await rollup(q, "date");
  return rows
    .map((r) => ({ ...r, ...deriveKpis(r), date: r.group_key }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export interface BreakdownRow extends RollupRow, Kpis {
  key: string;
  label: string;
}

/** Totals grouped by one dimension (brand / platform / region / activity). */
export async function getBreakdown(q: RollupQuery, dim: Exclude<RollupGroup, "total" | "date">): Promise<BreakdownRow[]> {
  const rows = await rollup(q, dim);
  return rows
    .map((r) => ({ ...r, ...deriveKpis(r), key: r.group_key, label: r.group_label }))
    // PostgREST decodes numeric/bigint as strings — coerce before adding,
    // otherwise "100" + "200" sorts as "100200".
    .sort((a, b) => Number(b.spend) + Number(b.revenue) - (Number(a.spend) + Number(a.revenue)));
}
